import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { dateTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

interface AuditRow {
  id: string;
  actorUserId: string | null;
  actorEmail: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  details: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

interface AuditPage {
  items: AuditRow[];
  total: number;
  limit: number;
  offset: number;
}

/** M0'da üretilen eylemler. Yeni modüller geldikçe genişler. */
const ACTIONS: Record<string, { label: string; tone: 'ok' | 'warn' | 'danger' | 'neutral' }> = {
  LOGIN_SUCCESS: { label: 'Giriş başarılı', tone: 'ok' },
  LOGIN_FAILED: { label: 'Giriş başarısız', tone: 'danger' },
  LOGOUT: { label: 'Çıkış', tone: 'neutral' },
  PASSWORD_CHANGED: { label: 'Şifre değişti', tone: 'warn' },
  TWO_FACTOR_ENABLED: { label: '2FA açıldı', tone: 'ok' },
  TWO_FACTOR_DISABLED: { label: '2FA kapatıldı', tone: 'warn' },
  USER_CREATED: { label: 'Kullanıcı eklendi', tone: 'warn' },
  USER_UPDATED: { label: 'Kullanıcı güncellendi', tone: 'warn' },
  SETTINGS_UPDATE: { label: 'Ayar değişti', tone: 'warn' },
};

const PAGE_SIZE = 50;

export function AuditLogPage() {
  const [action, setAction] = useState('');
  const [offset, setOffset] = useState(0);

  const page = useQuery({
    queryKey: ['audit-log', action, offset],
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (action) params.set('action', action);
      return api.get<AuditPage>(`/audit-log?${params.toString()}`);
    },
    // Sayfa değişirken tablo boşalıp zıplamasın.
    placeholderData: keepPreviousData,
  });

  const total = page.data?.total ?? 0;
  const shown = page.data?.items.length ?? 0;

  return (
    <>
      <PageHeader
        title="Denetim kaydı"
        description="Salt-ekleme. Veritabanı tetikleyicisi güncelleme ve silmeyi reddeder — kayıt silme ucu yoktur ve olmayacaktır."
        actions={
          <Select
            aria-label="Eylem filtresi"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">Tüm eylemler</option>
            {Object.entries(ACTIONS).map(([key, meta]) => (
              <option key={key} value={key}>
                {meta.label}
              </option>
            ))}
          </Select>
        }
      />

      <Card>
        {page.isPending ? (
          <div className="px-4 py-10 text-center">
            <Spinner size={20} />
          </div>
        ) : page.isError ? (
          <EmptyState title="Denetim kaydı yüklenemedi" />
        ) : page.data.items.length === 0 ? (
          <EmptyState
            title="Kayıt yok"
            description={action ? 'Bu eylem için kayıt bulunamadı.' : undefined}
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Zaman</Th>
                <Th>Eylem</Th>
                <Th>Yapan</Th>
                <Th>Nesne</Th>
                <Th>Ayrıntı</Th>
                <Th>IP</Th>
              </tr>
            </thead>
            <tbody>
              {page.data.items.map((row) => {
                const meta = ACTIONS[row.action];
                return (
                  <tr key={row.id}>
                    <Td className="whitespace-nowrap">{dateTime(row.createdAt)}</Td>
                    <Td>
                      <Badge tone={meta?.tone ?? 'neutral'}>{meta?.label ?? row.action}</Badge>
                    </Td>
                    <Td>{row.actorEmail ?? '—'}</Td>
                    <Td>{row.entityType ?? '—'}</Td>
                    <Td>
                      {row.details ? (
                        <code className="text-[11px] text-[var(--c-ink-soft)]">
                          {JSON.stringify(row.details)}
                        </code>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td className="whitespace-nowrap">{row.ipAddress ?? '—'}</Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      {total > PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-between text-xs text-[var(--c-ink-soft)]">
          <span>
            {offset + 1}–{offset + shown} / {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Önceki
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Sonraki
            </Button>
          </div>
        </div>
      )}

      <p className="mt-4 text-xs text-[var(--c-ink-faint)]">
        Ayar değişikliklerinde yalnızca <strong>anahtar adları</strong> kaydedilir, değerler
        kaydedilmez — API anahtarı gibi sırlar denetim kaydına sızmasın diye.
      </p>
    </>
  );
}
